import { useState } from "react";

import { registerPasskey } from "../auth/webauthnClient";

interface OfferPasskeyStepProps {
  accessToken: string;
  phone: string;
  onDone: () => void;
}

/**
 * Shown right after a fresh QR-confirmed login (see WebLoginQr.tsx) --
 * same "в следующий раз QR не понадобится" offer as kwms-web's own
 * OfferPasskeyStep, ported as-is (nodeowner-web has no other login path to
 * attach this to, only the QR one).
 */
export function OfferPasskeyStep({ accessToken, phone, onDone }: OfferPasskeyStepProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once flipped on (a real enrollment succeeded), there's no "off" here --
  // that would mean deleting the credential, a separate feature this
  // screen doesn't have.
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);

  // Flipping the switch on IS the enrollment -- unlike a typical settings
  // toggle there's no separate "save" step, the WebAuthn ceremony has to
  // happen right on this user gesture (a later, detached click wouldn't
  // count as one for the browser's own passkey APIs).
  async function togglePasskey() {
    if (isLoading || passkeyEnabled) return;
    setIsLoading(true);
    setError(null);
    try {
      await registerPasskey(accessToken, phone);
      setPasskeyEnabled(true);
    } catch (e) {
      // Login already succeeded -- a failed enrollment shouldn't block
      // getting into the app, just report it and let them retry or skip.
      setError(e instanceof Error ? e.message : "Не удалось включить вход по ключу доступа");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <div className="badge-scan-hint" style={{ paddingBottom: 0 }}>
        <div className="badge-scan-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </div>
        <p className="status-text" style={{ marginBottom: 0 }}>
          В следующий раз QR не понадобится — отпечаток, Face ID или ключ безопасности
        </p>
      </div>

      <hr className="divider" />

      <div className="toggle-row">
        <span className="field-value">Вход по ключу доступа</span>
        <button
          type="button"
          role="switch"
          aria-checked={passkeyEnabled}
          aria-label="Вход по ключу доступа"
          className="switch"
          onClick={togglePasskey}
          disabled={isLoading || passkeyEnabled}
        >
          <span className="switch-thumb" />
        </button>
      </div>
      {isLoading && !passkeyEnabled && <p className="helper-text">Подтвердите на устройстве…</p>}
      {error && <p className="error-text" role="alert">{error}</p>}

      <button className="btn btn-primary" style={{ width: "100%", marginTop: "16px" }} onClick={onDone} disabled={isLoading}>
        {passkeyEnabled ? "Готово" : "Продолжить"}
      </button>
    </div>
  );
}
